import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { TelemetryService } from 'services/telemetry.service';
import { FoodDbService } from 'services/food-db.service';
import { FoodReadFailurePolicy } from 'services/food-read-error';
import { ReportedSearchFailureError } from 'services/food-search-error';
import { ConfigDbService } from 'services/config-db.service';
import { DebugService } from 'services/debug.service';
import { SettingsService } from 'services/settings.service';
import { CameraInitResult } from 'services/camera.service';
import { LoggingService } from 'services/logging.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { runScanFailureRecoveryAsync } from 'services/scan-error-policy';
import { showScanRetryAlertAsync } from 'services/scan-error-alert';
import { UserDb } from 'services/user-db.service';
import { WakeLockService } from 'services/wake-lock.service';
import { ScanMode } from 'services/scan-config';
import { ScannedFoodMatch, ScannedFoodMatchSource, matchScannedFoodWithSourceAsync, mealScanAsksForServerPicks, scannerFoodHeader } from 'services/scanner-food-match';
import { describeError } from '@cf-shared/error-describe';
import { StapleDbService } from 'services/staple-db.service';
import { ScannerModelSelection } from 'services/scanner-model-selection';
import { ScannerPreferenceMemory } from 'services/scanner-preference-memory';
import { FakeScanProgress } from 'services/scan-progress';
import { MultiModelFallbackManager, runMultiModelScanAsync } from 'services/multi-model-fallback';
import { MEAL_SCAN_MODEL_TRY_ORDER } from './meal-scan-model-order';
import { openScannerImagePicker } from 'services/scanner-file-picker';
import { CsCameraViewComponent } from 'components/cs-camera-view/cs-camera-view';
import { CsScannerCameraStageComponent } from 'components/cs-scanner-camera-stage/cs-scanner-camera-stage';
import { ScanMealPhotoResults, DetectedFoodItem, MealScanFoodPick } from '@cf-shared/scan-meal-photo-interface';
import { ScanQuotaRejection, ScanLabelError } from '@cf-shared/scan-label-interface';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { FoodHeader } from '@cf-shared/data-model/food';
import { scannedRowDisplayNutrients } from 'services/scanned-row-nutrients';
import { IFoodsPage, FoodsPageResult } from 'pages/foods/foods-interface';
import { ErrandAnswerShape } from 'pages/foods/add-food-errand';
import { ILabelScannerPage } from 'pages/label-scanner-page/label-scanner-interface';
import { MealPhotoScannerPageParams, MealPhotoScannerResult } from './meal-photo-scanner-interface';
import { EditIngredientMode, IEditIngredientPage, EditIngredientResult, EditIngredientReturnCapability } from 'pages/edit-ingredient/edit-ingredient-interface';
import { typedWeight } from 'pages/edit-ingredient/weighed-with-refuse';
import { IngredientReal, IngredientType, isIngredientReal } from '@cf-shared/data-model/ingredient';
import { OnlineStateService } from 'services/online-state.service';
import { mealIngredientsBesidesTheAdjustedRow } from './meal-scan-running-total';
import { summarizeScanGramEdits } from './meal-scan-gram-edits';
import { findScannedRowById, mintScannedRowId } from 'services/scanner-row-identity';
import { ScannerName } from 'services/camera-permission-help';
import { defaultIngredientForFood } from 'services/default-ingredient-for-food';
import { MealScanResultRow, buildHandAddedRow, isHandAddedRow, rowsThatTeachThePreferenceMemory, scanRowsToMatchedFoods, scannedRowsGoingIntoTheMeal } from './meal-scan-added-row';

type ResultItem = MealScanResultRow;

/** The fixed scan result the test-mode MOCK button puts on the page (#6807): what the AI names
 *  for a plain plate of chicken and rice. */
const MOCK_MEAL_SCAN_FOODS: DetectedFoodItem[] = [
  { name: 'grilled chicken breast', estimatedGrams: 150, confidence: 0.9 },
  { name: 'white rice', estimatedGrams: 180, confidence: 0.8 },
];

@Component({
  selector: 'page-meal-photo-scanner',
  templateUrl: 'meal-photo-scanner-page.html',
  host: { 'class': 'meal-photo-scanner cs-scanner-page' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MealPhotoScannerPage extends CsPage<MealPhotoScannerPageParams> implements AfterViewInit {
  protected ScannerName = ScannerName;
  protected LL = LL;

  @ViewChild('stage') stage: CsScannerCameraStageComponent;
  cameraPermissionDismissed = false;

  /** the wrapped camera view inside the shared camera stage */
  get cameraView(): CsCameraViewComponent | undefined {
    return this.stage?.cameraView;
  }

  phase: 'camera' | 'results' = 'camera';
  /** at most one photo — the meal scanner is single-shot */
  capturedPhotos: { dataUrl: string; base64: string }[] = [];
  get capturedPhoto(): { dataUrl: string; base64: string } | null {
    return this.capturedPhotos[0] ?? null;
  }
  analyzing = false;

  resultItems: ResultItem[] = [];
  // whether the last scan asked the server to pick the database foods (#5623); sent with the
  // result and replace events so the pick can be measured (#6656)
  private scanAskedForPicks = false;
  // the user's remembered food picks for AI-detected food names, shared with the
  // recipe scanner (df-bc08ed83dd29). Each scanner keeps its own `_local` document.
  private readonly prefMemory = new ScannerPreferenceMemory('_local/meal_scan_preferences', 'Meal-scan');

  // model selection (#3783 — unified scanner model config + sequential
  // fallback). Multi mode tries the configured models in turn with a 1-hour
  // blacklist (see multi-model-fallback.ts); Fixed/All/Any pass fixedModel
  // straight through. This replaces the old single random "coin flip" pick.
  // Non-private: the template reads it to render the shared model picker.
  readonly modelSelection = new ScannerModelSelection();
  private multiModelFallback = new MultiModelFallbackManager();
  /** Shown in the analyzing overlay while falling back to the next model. */
  retryMessage: string = null;

  // shared fake-progress driver (meal shows a single static status line)
  private fakeProgress = new FakeScanProgress(
    [() => LL.mealPhotoScanner.analyzing()],
    () => this.onProgressTick(),
  );

  get analyzeProgress(): number { return this.fakeProgress.progress; }

  /** Test-mode only: the MOCK button that puts a fixed scan result on the page (#6807), as the
   *  recipe scanner's does. */
  protected get showMockButton(): boolean { return this.settings.testMode; }
  /** label for the dev MOCK button, built here rather than translated because only testers see it */
  protected mockButtonLabel: TranslatedString = untranslatedString('MOCK (dev only)');

  constructor(
    private settings: SettingsService,
    private logging: LoggingService,
    private navCtrl: NavController,
    private cloudFunctions: CloudFunctionsService,
    private telemetry: TelemetryService,
    private foodDb: FoodDbService,
    private configDb: ConfigDbService,
    private debug: DebugService,
    private alertCtrl: AlertController,
    private userDb: UserDb,
    private cd: ChangeDetectorRef,
    private wakeLock: WakeLockService,
    private onlineState: OnlineStateService,
    private stapleDb: StapleDbService,
  ) {
    super();
    // Build the device's staple copy NOW, while the page is opening, so a scan never pays for it
    // (food staples plan 4c, #5625, Requirement C6). The reads in `tryMatchFoodAsync` are then
    // answered from memory. Not awaited and nothing is reported here: the store contains and
    // reports its own failures, and a scan works without the staples exactly as it did before.
    // Caught, and REPORTED: the store contains and reports the failures it anticipates, so anything
    // arriving here is something it did not — and an empty catch would lose exactly those.
    void this.stapleDb.warmAsync().catch(error =>
      this.logging.trackSilentProblem('staple warm-up failed', { error: describeError(error) }));
  }

  private isReplaceMode = false;

  get checkedCount(): number {
    return this.resultItems.filter(i => i.checked).length;
  }

  get actionButtonLabel(): TranslatedString {
    const count = this.checkedCount;
    return this.isReplaceMode
      ? LL.mealPhotoScanner.replaceInMeal({ count })
      : LL.mealPhotoScanner.addToMeal({ count });
  }

  async ngAfterViewInit() {
    this.isReplaceMode = this.navParams.data.mode === 'replace';
    await this.modelSelection.initAsync(this.configDb, this.settings);
    if (this.viewDestroyed) return;
    await this.prefMemory.loadAsync(this.userDb.database, this.logging);
  }

  // the shared cs-scanner-model-picker emits the chosen key; the shared
  // selection persists it to the one setting every scanner reads (#3783)
  onModelSelected(key: string) {
    this.modelSelection.onModelSelected(key, this.settings);
    this.cd.markForCheck();
  }

  // ── Camera ──────────────────────────────────────────────────────────

  onCameraStateChange() {
    this.cameraPermissionDismissed = this.cameraView?.cameraService.cameraPermissionDismissed ?? false;
    this.cd.markForCheck();
  }

  onCameraError(result: CameraInitResult) {
    if (result === CameraInitResult.NoCameraWorked) {
      if (this.cameraView?.cameraService.permissionStatus?.state === 'granted') {
        void this.showErrorAlertAsync(LL.mealPhotoScanner.error());
      }
    }
  }

  // ── Photo capture ─────────────────────────────────────────────────

  // haptic + shutter sound are handled by cs-scanner-shutter-bar
  async snapPhoto() {
    this.capturedPhotos = [await this.cameraView.capturePhoto()];
    void this.cameraView.stopCamera();
    this.telemetry.trackEvent('meal_photo_scan_photo_taken', { source: 'camera' });
    this.cd.markForCheck();
  }

  openFilePicker() {
    openScannerImagePicker({
      safeTimeout: (callback, ms) => this.safeTimeout(callback, ms),
      onPicked: photo => {
        this.capturedPhotos = [photo];
        void this.cameraView?.stopCamera();
        this.telemetry.trackEvent('meal_photo_scan_photo_taken', { source: 'upload' });
        if (this.viewDestroyed) return;
        this.cd.markForCheck();
      },
    });
  }

  retakePhoto() {
    this.capturedPhotos = [];
    this.cd.markForCheck();
    void this.cameraView?.reinitCamera();
  }

  // from the results screen: discard the results and start over with the
  // camera. The camera stage remounts when the phase flips back, and the
  // fresh cs-camera-view starts the camera itself (same as page entry).
  retakeFromResults() {
    this.resultItems = [];
    this.capturedPhotos = [];
    this.phase = 'camera';
    this.cd.markForCheck();
  }

  // ── Analysis ─────────────────────────────────────────────────────

  // submit the captured photo to the meal-photo scanner cloud function and
  // route the resulting foods through the match-and-render pipeline. Holds
  // the screen awake for the duration of the round-trip (release in finally).
  async analyzePhoto() {
    if (!this.capturedPhoto || this.analyzing) return;

    this.analyzing = true;
    this.retryMessage = null;
    this.fakeProgress.start();
    this.telemetry.trackEvent('meal_photo_scan_start', {
      model: this.modelSelection.scanMode === ScanMode.Multi ? this.modelSelection.multiModels.join(',') : this.modelSelection.fixedModel,
    });
    this.cd.markForCheck();

    try {
      // hold screen awake for the cloud-functions round-trip + model inference
      // (~5-15s on a good network). iOS auto-lock would otherwise suspend the
      // in-flight fetch mid-way. Inside the try so finally is the single
      // release site.
      void this.wakeLock.acquireAsync('scanner:meal-photo');
      const result = await this.runScanAsync();
      // runScanAsync returns null only when the page went away mid-scan
      if (!result || this.viewDestroyed) return;

      // a multi-model offline failure comes back as a ProviderError result (not
      // a thrown error). The shared policy keeps the photo + offers Try Again.
      if (result.error && !this.onlineState.isLikelyOnline()) {
        this.stopAnalyzing();
        this.telemetry.trackEvent('meal_photo_scan_error', { error: 'network' });
        await this.handleScanFailureAsync(result.error);
        return;
      }

      // handle hard quota rejection (skip QualityWarning — it's a soft warning, scan proceeds)
      if (result.quota?.rejection && result.quota.rejection !== ScanQuotaRejection.QualityWarning) {
        this.stopAnalyzing();
        await this.handleQuotaRejection(result);
        this.cd.markForCheck();
        return;
      }

      // provider / network errors: the shared policy keeps the photo and offers a
      // Try Again for transient failures (provider error / timeout / incomplete),
      // and resets to a fresh camera for the non-retryable ones.
      if (result.error) {
        this.stopAnalyzing();
        this.telemetry.trackEvent('meal_photo_scan_error', { error: result.error });
        await this.handleScanFailureAsync(result.error);
        return;
      }

      // handle no foods
      if (!result.foods || result.foods.length === 0) {
        this.stopAnalyzing();
        this.telemetry.trackEvent('meal_photo_scan_no_foods', { reason: result.noFoodsReason });
        // a packaged-product photo gets its own alert that offers the matching
        // scanner — the AI tells us whether it saw a barcode or a nutrition
        // label (#3902); blurry and generic no-food results keep the plain
        // retake alert.
        if (result.noFoodsReason === 'barcode' || result.noFoodsReason === 'nutrition_label') {
          // map the detected packaging kind to the scanner the alert opens
          const scanner = result.noFoodsReason === 'barcode' ? 'barcode' : 'label';
          await this.showPackagedProductAlertAsync(scanner);
        } else {
          const message = result.noFoodsReason === 'blurry'
            ? LL.mealPhotoScanner.noFoodsBlurry()
            : LL.mealPhotoScanner.noFoodsDetected();
          await this.showErrorAlertAsync(message, true);
        }
        this.cd.markForCheck();
        return;
      }

      // success — match foods. Keep the analyzing overlay (and its still-
      // ticking progress bar) up through the local DB / Elasticsearch
      // matching, so the bar only completes when the results view is
      // actually ready to show — no idle gap on the camera screen.
      this.telemetry.trackEvent('meal_photo_scan_success', { foodCount: result.foods.length, model: result.modelKey });
      await this.matchFoodsAsync(result.foods);
      if (this.viewDestroyed) return;
      this.stopAnalyzing();
      this.phase = 'results';
      this.cd.markForCheck();

    } catch (err) {
      this.stopAnalyzing();
      this.telemetry.trackEvent('meal_photo_scan_error', { error: 'network' });
      // a real food-search failure during matching has already been reported by the search
      // client, which throws ReportedSearchFailureError to say so. Reporting it again here
      // filed a second Sentry event for one cause. Every other failure of the scan still
      // reports exactly as before.
      //
      // This skips MORE than the duplicate event, and that was a deliberate decision
      // (owner, 2026-09-07). trackAbnormalException is called here with no userMessage, so it
      // also shows the generic "we noticed something unusual, would you like to report it?"
      // toast. For this one failure class that toast is gone. It was a second interruption
      // about a failure this page already reports in the user's own language, right below —
      // handleScanFailureAsync puts up the scan-failure screen with a Try Again. The same
      // argument settled df-cfd308fcb9a2 for the same failure one layer down.
      //
      // What is NOT lost, so nobody has to re-derive it: the failure is still reported once,
      // with the full error text, by the search client; the app-review-prompt flag is still
      // set, because trackSilentProblem routes through noteSilentProblem and the search client does
      // not mark this report SessionHealth.NotTheUsersSession; and a sentry_error Mixpanel row is still sent,
      // as kind: problem rather than kind: error. What the user loses is the one-tap button
      // that would have attached their own bug report to the Sentry event.
      if (!(err instanceof ReportedSearchFailureError)) {
        this.logging.trackAbnormalException(err);
      }
      // a thrown request error is transient; pass ProviderError as the stand-in
      // and let the shared policy keep the photo + offer Try Again (offline copy
      // when the connectivity signal says we are offline).
      await this.handleScanFailureAsync(ScanLabelError.ProviderError);
    } finally {
      void this.wakeLock.releaseAsync('scanner:meal-photo');
    }
  }

  // Single-call or multi-model fallback (shared blacklist + retry loop; see
  // multi-model-fallback.ts). Unlike the other scanners, the models are tried
  // in the configured order, not shuffled (#6810, meal-scan-model-order.ts).
  // In Multi mode a provider-side failure
  // on one model falls back to the next instead of failing the whole scan —
  // this is what removes the old single random "coin flip". Returns null only
  // when the page went away mid-scan.
  private async runScanAsync(): Promise<ScanMealPhotoResults | null> {
    const imageBase64 = this.capturedPhoto.base64;
    // whether the server should also pick a database food for each food it names (#5623); left out
    // of the request entirely when off, so the request is the one older servers always received
    this.scanAskedForPicks = mealScanAsksForServerPicks(this.configDb);
    const pickFoods = this.scanAskedForPicks ? true : undefined;

    if (this.modelSelection.scanMode === ScanMode.Multi) {
      return runMultiModelScanAsync<ScanMealPhotoResults>({
        models: this.modelSelection.multiModels,
        manager: this.multiModelFallback,
        tryOrder: MEAL_SCAN_MODEL_TRY_ORDER,
        isOnline: () => this.onlineState.isLikelyOnlineOrRecheckAsync(),
        scanAsync: (modelKey, attempt, total) => {
          this.telemetry.trackEvent('meal_photo_scan_start', { model: modelKey, multi: true, attempt, total });
          console.log(`Meal scan: trying ${modelKey} (attempt ${attempt + 1}/${total})`);
          // the server does not read `locale` for this scanner yet — the identified food name
          // is a generic-English database search key. Sent so a future framing hook can use it (#4206)
          return this.cloudFunctions.scanMealPhotoAsync({ imageBase64, model: modelKey, locale: BOOT_LOCALE, pickFoods });
        },
        isProviderSideError: result =>
          result.error === ScanLabelError.ProviderError || result.error === ScanLabelError.ProviderTimeout || result.error === ScanLabelError.IncompleteResponse,
        makeNetworkErrorResult: () => ({ error: ScanLabelError.ProviderError }),
        isAborted: () => this.viewDestroyed,
        onRetry: () => this.showRetryFeedback(),
        onNetworkError: (modelKey, err) =>
          console.log(`Meal scan: ${modelKey} network error:`, (err as Error)?.message),
        onProviderError: (modelKey, result) =>
          console.log(`Meal scan: ${modelKey} returned error ${result.error}, blacklisting for 1hr`),
      });
    }

    // single-model or 'all' mode passes the configured model directly
    return this.cloudFunctions.scanMealPhotoAsync({ imageBase64, model: this.modelSelection.fixedModel, locale: BOOT_LOCALE, pickFoods });
  }

  // show "retrying with a different model" feedback and restart the progress
  // bar from zero for the next model attempt
  private showRetryFeedback() {
    this.retryMessage = LL.mealPhotoScanner.retryingDifferentServer();
    this.fakeProgress.start();
    this.cd.markForCheck();
  }

  private async handleQuotaRejection(result: ScanMealPhotoResults) {
    let message: TranslatedString;
    switch (result.quota.rejection) {
      case ScanQuotaRejection.AccountLocked:
        message = LL.mealPhotoScanner.quotaAccountLocked();
        break;
      case ScanQuotaRejection.RateLimited:
        message = LL.mealPhotoScanner.quotaRateLimited();
        break;
      case ScanQuotaRejection.DailyCooldown:
        message = LL.mealPhotoScanner.quotaDailyCooldown();
        break;
      case ScanQuotaRejection.GlobalCap:
        message = LL.common.messages.scanAppCapacityReached();
        break;
      case ScanQuotaRejection.QualityWarning:
        // warning only — scan proceeded, shouldn't reach here
        return;
    }
    await this.alertCtrl.showAlertAsync({
      title: LL.mealPhotoScanner.quotaTitle(),
      message,
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });
  }

  /** Dev-only (test mode) MOCK entry point (#6807): puts a fixed scan result where the real
   *  success path would, then runs the SAME food matching, so the result screen and everything
   *  after it can be tried and E2E-tested with no camera and no AI call. Only the AI call is
   *  skipped. */
  async mockAnalyzeAsync() {
    if (!this.showMockButton || this.analyzing) return;
    // stop the live preview if it runs: the mock has no photo
    if (this.cameraView?.isActive) {
      await this.cameraView.stopCamera();
      if (this.viewDestroyed) return;
    }
    this.analyzing = true;
    this.fakeProgress.start();
    this.cd.markForCheck();
    try {
      await this.matchFoodsAsync(MOCK_MEAL_SCAN_FOODS);
    } finally {
      this.stopAnalyzing();
    }
    if (this.viewDestroyed) return;
    this.phase = 'results';
    this.cd.markForCheck();
  }

  // ── Food matching ─────────────────────────────────────────────────

  // match each AI-detected food against preference memory, local DB, then Elasticsearch
  private async matchFoodsAsync(detectedFoods: DetectedFoodItem[]) {
    const items: ResultItem[] = [];
    let matchedCount = 0;
    let unmatchedCount = 0;
    // how many rows each source filled, so the server's pick can be measured (#6656)
    const sourceCounts = new Map<ScannedFoodMatchSource, number>();

    for (const food of detectedFoods) {
      const normalizedName = food.name.toLowerCase().trim();
      const subtitle = LL.mealPhotoScanner.estimatedGrams({ grams: food.estimatedGrams });

      const match = await this.tryMatchFoodAsync(food.name, normalizedName, food.pick);
      sourceCounts.set(match.source, (sourceCounts.get(match.source) ?? 0) + 1);
      if (match.header) {
        matchedCount++;
        const item = this.createResultItem(food, subtitle, match.header, match.source);
        await this.loadRowFoodAsync(item);
        items.push(item);
      } else {
        unmatchedCount++;
        const placeholder: FoodHeader = { _id: '', name: food.name, displayNutrients: [], recipe: false, status: undefined };
        items.push({ ...this.createResultItem(food, subtitle, placeholder, match.source), matched: false, checked: false, foodId: undefined });
      }
    }

    this.resultItems = items;
    this.telemetry.trackEvent('meal_photo_scan_match_results', {
      matched: matchedCount,
      unmatched: unmatchedCount,
      total: detectedFoods.length,
      askedForPicks: this.scanAskedForPicks,
      remembered: sourceCounts.get(ScannedFoodMatchSource.Remembered) ?? 0,
      serverPicked: sourceCounts.get(ScannedFoodMatchSource.ServerPick) ?? 0,
      serverNone: sourceCounts.get(ScannedFoodMatchSource.ServerNone) ?? 0,
      phoneRule: sourceCounts.get(ScannedFoodMatchSource.PhoneRule) ?? 0,
      noMatch: sourceCounts.get(ScannedFoodMatchSource.NoMatch) ?? 0,
    });
  }

  // try matching a food name: the food the user picked before for it, else the server's pick when
  // the scan carried one (#5623), else the best search match. Shared with the recipe scanner, which
  // does the same thing without a server pick — see services/scanner-food-match.ts.
  private async tryMatchFoodAsync(name: string, normalizedName: string, serverPick: MealScanFoodPick | undefined): Promise<ScannedFoodMatch> {
    return matchScannedFoodWithSourceAsync({
      name,
      normalizedName,
      serverPick,
      prefMemory: this.prefMemory,
      services: { foodDb: this.foodDb, configDb: this.configDb, debug: this.debug, stapleDb: this.stapleDb, logging: this.logging },
      connectionProblemName: 'Meal-scan food match: connection error',
    });
  }

  private createResultItem(food: DetectedFoodItem, subtitle: TranslatedString, header: FoodHeader, matchSource: ScannedFoodMatchSource): ResultItem {
    return {
      rowId: mintScannedRowId(),
      aiName: food.name,
      aiEstimatedGrams: food.estimatedGrams,
      estimatedGrams: food.estimatedGrams,
      confidence: food.confidence,
      matched: true,
      checked: true,
      foodHeader: header,
      subtitle,
      foodId: header._id,
      matchSource,
    };
  }

  // open edit ingredient page to adjust weight/serving
  onItemClick(item: ResultItem) {
    if (!item.matched || !item.foodId) return;

    const ingredient: IngredientReal = {
      type: IngredientType.Real,
      foodId: item.foodId,
      name: item.foodHeader.name,
      weight_g: item.estimatedGrams,
      // a row ticked "Weighed with peel" before reopens ticked, with the weight as typed
      ...(item.weighedWithRefuse ? { weighedWithRefuse: item.weighedWithRefuse } : {}),
    };

    // Everything the meal will hold besides this one food: what the meal already has
    // when the scan will keep it, plus the other scanned foods the user has left
    // ticked. That list is what the portion screen totals as the meal, and its `Day`
    // scope puts that total in place of the meal's whole contribution to the day -- so
    // handing it an empty list, as this page used to, would have shown a day figure
    // missing everything but the food being adjusted.
    const local = this.navParams.data.local;
    const otherIngredients = mealIngredientsBesidesTheAdjustedRow(
      this.resultItems, item, local?.mealIngredients, this.isReplaceMode);

    void this.navCtrl.pushAsync(IEditIngredientPage, {
      ingredientIndex: -1,
      mode: EditIngredientMode.New,
      // this screen is opened for the WEIGHT of this one scanned row, and a scanned row holds
      // exactly one food, so the portion screen must not offer to split a recipe into several
      // (deferred finding df-d3f1a6b577ee). Its Split buttons used to appear here and do
      // nothing at all.
      returnCapability: EditIngredientReturnCapability.SingleIngredientOnly,
      returnCallback: 'onIngredientEdited',
      local: {
        ingredients: otherIngredients,
        targets: local?.mealTargets,
        pendingIngredient: ingredient,
        returnContext: { rowId: item.rowId },
        ...this.dayScopeParams(),
      },
    });
  }

  /**
   * The three values the portion screen's `Day` scope needs, forwarded untouched from
   * whoever opened this scanner, or nothing at all.
   *
   * Built together so the **all three or none** contract on
   * `EditIngredientPageParams.local` holds rather than being merely intended: passing them
   * one by one would let a caller that set only the meal name send a name beside no day.
   */
  private dayScopeParams(): { dayDate?: string, mealId?: string, mealName?: string } {
    const local = this.navParams.data.local;
    if (!local?.dayDate || !local?.mealId) return {};
    return { dayDate: local.dayDate, mealId: local.mealId, mealName: local.mealName };
  }

  /**
   * Callback from the portion screen: apply what the user did to the scanned row it was opened
   * on.
   *
   * That screen can answer with more than a saved amount, and every answer it gives has to be
   * either applied here or reported. Until deferred finding df-d3f1a6b577ee was fixed, this
   * handler returned unless the action was `save`, so the Split buttons and the More menu's
   * Remove item popped the screen and changed nothing at all, with no message to the user and
   * nothing recorded for us. Split is no longer offered on this path at all — the push above
   * says so with `returnCapability` — so a split arriving here would mean that suppression has
   * come undone.
   */
  async onIngredientEdited(context: { rowId: string }, result: EditIngredientResult) {
    // no result at all is the user leaving the screen without choosing anything. That is
    // ordinary, and reporting it would bury the answers that really cannot be applied.
    if (!result) return;

    if (result.action !== 'save' && result.action !== 'delete') {
      this.logging.trackSilentProblem('Meal-scan portion screen returned an action the scanner cannot apply', {
        action: result.action,
      });
      return;
    }
    if (result.action === 'save' && !result.ingredient) {
      this.logging.trackSilentProblem('Meal-scan portion screen saved with no ingredient', { rowId: context.rowId });
      return;
    }

    const item = findScannedRowById(this.resultItems, context.rowId);
    if (!item) return;

    if (result.action === 'delete') {
      // "Remove" on the portion screen means "do not log this food". The scanned row is not in
      // any meal yet, so unticking it is exactly that — and the user can tick it again from the
      // list, which deleting the row outright would not allow.
      item.checked = false;
    } else {
      item.estimatedGrams = result.ingredient.weight_g;
      // the eaten weight above was converted from a weight taken with the peel if this is set (#5544)
      item.weighedWithRefuse = isIngredientReal(result.ingredient) ? result.ingredient.weighedWithRefuse : undefined;
      this.refreshRowSubtitle(item);
      this.refreshRowNutrients(item);
      item.checked = true;
    }

    if (this.viewDestroyed) return;
    this.cd.markForCheck();
  }

  onItemToggled(item: ResultItem, checked: boolean) {
    item.checked = checked;
    this.cd.markForCheck();
  }

  // ── Replace/Search food ───────────────────────────────────────────

  // open food search to replace a matched food or find an unmatched one
  replaceFood(item: ResultItem) {
    this.openFoodSearchAsync(item, 'onFoodReplaced');
  }

  searchFood(item: ResultItem) {
    this.openFoodSearchAsync(item, 'onFoodSearched');
  }

  private openFoodSearchAsync(item: ResultItem, returnCallback: string) {
    void this.navCtrl.pushAsync(IFoodsPage, {
      mode: 'select',
      returnCallback,
      local: { returnContext: { rowId: item.rowId } },
      // a row the user added by hand has no AI name, so search for the food it holds
      initialSearchText: item.aiName || item.foodHeader.name,
    });
  }

  // ── Add a food the scan missed (#6807) ────────────────────────────

  // open food search for a food that is on the plate but not in the list. The pick then goes
  // through the portion screen, as a food added on the meal page does, and only a saved amount
  // adds the row.
  addMissedFood() {
    void this.navCtrl.pushAsync(IFoodsPage, {
      mode: 'select',
      returnCallback: 'onMissedFoodPicked',
      // the Foods page reports a pick that comes back with no context as a lost one, so give it
      // one, as openFoodSearchAsync does. onMissedFoodPicked does not read it.
      local: { returnContext: { source: 'missedFood' } },
    });
  }

  // callback from FoodsPage: open the portion screen on the picked food, at the amount the meal
  // page would start it on
  async onMissedFoodPicked(_context: unknown, result: FoodsPageResult) {
    if (!result || result.action !== 'select') return;

    const food = await this.foodDb.getFoodByIdAsync(result.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
    if (this.viewDestroyed) return;
    if (!food) {
      // the user picked a food and nothing would happen: say so, and report it
      this.reportHandAddedFoodMissing(result.foodId);
      return;
    }

    const ingredient = defaultIngredientForFood(food, food.status || this.foodDb.getFoodStatus(food._id));
    const local = this.navParams.data.local;
    // the scanned rows the user has left ticked plus what the meal already holds, as for a row
    // tap. no row is being adjusted, so none is left out.
    const otherIngredients = mealIngredientsBesidesTheAdjustedRow(
      this.resultItems, undefined, local?.mealIngredients, this.isReplaceMode);

    void this.navCtrl.pushAsync(IEditIngredientPage, {
      ingredientIndex: -1,
      mode: EditIngredientMode.New,
      // a row holds exactly one food, so the portion screen must not offer to split a recipe
      // (see onItemClick)
      returnCapability: EditIngredientReturnCapability.SingleIngredientOnly,
      returnCallback: 'onMissedFoodPortion',
      local: {
        ingredients: otherIngredients,
        targets: local?.mealTargets,
        pendingIngredient: ingredient,
        returnContext: { foodId: food._id },
        ...this.dayScopeParams(),
      },
    });
  }

  // callback from the portion screen opened by onMissedFoodPicked: a saved amount adds the food as
  // a ticked row. Leaving the screen, or Remove, adds nothing, because nothing was added yet.
  async onMissedFoodPortion(context: { foodId: string }, result: EditIngredientResult) {
    if (!result || result.action === 'delete') return;

    if (result.action !== 'save') {
      this.logging.trackSilentProblem('Meal-scan portion screen returned an action the scanner cannot apply', {
        action: result.action,
      });
      return;
    }
    if (!result.ingredient || !isIngredientReal(result.ingredient)) {
      this.logging.trackSilentProblem('Meal-scan portion screen saved no food for a hand-added row', { foodId: context.foodId });
      return;
    }

    const food = await this.foodDb.getFoodByIdAsync(result.ingredient.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
    if (this.viewDestroyed) return;
    if (!food) {
      // the amount the user just saved would be dropped: say so, and report it
      this.reportHandAddedFoodMissing(result.ingredient.foodId);
      return;
    }

    const grams = result.ingredient.weight_g;
    const row = buildHandAddedRow({
      rowId: mintScannedRowId(),
      food: scannerFoodHeader(food),
      grams,
      weighedWithRefuse: result.ingredient.weighedWithRefuse,
      subtitle: LL.mealPhotoScanner.addedGrams({ grams: Math.round(grams) }),
    });
    // the row's numbers for its grams, like every other row (#6806)
    row.food = food;
    this.refreshRowNutrients(row);
    this.resultItems = [...this.resultItems, row];
    // which foods the scan misses, for the scan quality work (#6191). the id only, never the name,
    // so the names of a user's own foods stay out of Mixpanel
    this.telemetry.trackEvent('meal_photo_scan_food_added_by_hand', { foodId: food._id, askedForPicks: this.scanAskedForPicks });
    this.cd.markForCheck();
  }

  // a food the user chose for the Add Food row could not be loaded (deleted since the last sync, or
  // the read failed), so no row can be added. tell the user and report it, as the meal page does
  // for a food it cannot add.
  private reportHandAddedFoodMissing(foodId: string) {
    this.logging.trackAbnormalError(`Meal-scan hand-added food could not be loaded: ${foodId}`, LL.errors.userFacing.foodNotExist());
  }

  // callback from FoodsPage after user picks a replacement or searches for unmatched food
  async onFoodReplaced(context: { rowId: string }, result: FoodsPageResult) {
    await this.handleFoodSelectionAsync(context, result, 'meal_photo_scan_replaced');
  }

  async onFoodSearched(context: { rowId: string }, result: FoodsPageResult) {
    await this.handleFoodSelectionAsync(context, result);
  }

  /**
   * The row's grams line: the AI estimate alone, or the estimate and the user's own value once they
   * differ. Shown and compared in whole grams, because a weight ticked "Weighed with peel" (#5544) is
   * an eaten weight worked out from the typed one and is rarely a whole number.
   */
  private refreshRowSubtitle(item: ResultItem) {
    const measured = Math.round(item.estimatedGrams);
    // a row the user added by hand never had an AI estimate to compare against
    if (isHandAddedRow(item)) {
      item.subtitle = LL.mealPhotoScanner.addedGrams({ grams: measured });
      return;
    }
    // if the user changed the weight, show both the AI estimate and the user value
    if (measured !== Math.round(item.aiEstimatedGrams)) {
      item.subtitle = LL.mealPhotoScanner.estimatedToMeasured({ estimated: item.aiEstimatedGrams, measured });
    } else {
      item.subtitle = LL.mealPhotoScanner.estimatedGrams({ grams: measured });
    }
  }

  /**
   * Load the row's full food, so its numbers can be shown for its grams rather than per 100 g
   * (#6806). The same read `onMealPhotoScanResult` does on save, so it also warms that read.
   */
  private async loadRowFoodAsync(item: ResultItem) {
    item.food = await this.foodDb.getFoodByIdAsync(item.foodHeader._id, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing }) ?? undefined;
    this.refreshRowNutrients(item);
  }

  // the row's calories and macros for its current grams. if the food could not be loaded, show
  // none: the header's own numbers are per 100 g and would read as the amount on the plate
  private refreshRowNutrients(item: ResultItem) {
    item.nutrients = item.food ? scannedRowDisplayNutrients(item.food, item.estimatedGrams) : [];
  }

  // shared handler for replace/search food selection callbacks
  private async handleFoodSelectionAsync(context: { rowId: string }, result: FoodsPageResult, telemetryEvent?: string) {
    if (!result || result.action !== 'select') return;

    const food = await this.foodDb.getFoodByIdAsync(result.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
    if (!food || this.viewDestroyed) return;

    const item = findScannedRowById(this.resultItems, context.rowId);
    if (!item) return;

    item.foodHeader = scannerFoodHeader(food);
    item.food = food;
    item.foodId = result.foodId;
    item.matched = true;
    item.checked = true;
    // if the row was ticked "Weighed with peel", its weight is the old food's eaten weight, worked out
    // with the old food's uneaten share. that share does not apply to the new food, so put back the
    // weight that was on the scale and drop the tick (#5544)
    if (item.weighedWithRefuse) {
      // working the weight back can land a hair off the number typed (43.00000000000001), so drop that noise
      item.estimatedGrams = Math.round(typedWeight(item.estimatedGrams, item.weighedWithRefuse.percent) * 1000) / 1000;
      item.weighedWithRefuse = undefined;
      this.refreshRowSubtitle(item);
    }
    this.refreshRowNutrients(item);

    // the source the row had before this choice, so a replaced food is counted by who chose it (#6656)
    const replacedSource = item.matchSource;

    // a row the user added by hand stays one: it has no AI name for the preference memory to
    // file the new choice under (#6807)
    if (!isHandAddedRow(item)) {
      item.matchSource = ScannedFoodMatchSource.UserChosen;
      await this.prefMemory.recordPickAsync(item.aiName, result.foodId, food.name, this.userDb.database, this.logging);
    }
    if (telemetryEvent) {
      this.telemetry.trackEvent(telemetryEvent, { aiName: item.aiName, foodId: result.foodId, source: replacedSource, askedForPicks: this.scanAskedForPicks });
    }
    if (this.viewDestroyed) return;
    this.cd.markForCheck();
  }

  // ── Product-label guide (#3902) ────────────────────────────────────

  // The photo was a packaged product, not a plate of food. The AI told us which
  // kind it saw — a barcode or a nutrition label — so we offer the single
  // matching scanner in a simple alert (one primary action + cancel). On
  // confirm we redirect to that scanner; on cancel we just let the user retake.
  private async showPackagedProductAlertAsync(scanner: 'barcode' | 'label') {
    const isBarcode = scanner === 'barcode';
    const action = await this.alertCtrl.showAlertAsync<'scan' | 'cancel'>({
      title: isBarcode ? LL.mealPhotoScanner.barcodeDetectedTitle() : LL.mealPhotoScanner.nutritionLabelDetectedTitle(),
      message: isBarcode ? LL.mealPhotoScanner.barcodeDetectedMessage() : LL.mealPhotoScanner.nutritionLabelDetectedMessage(),
      buttons: [
        { text: isBarcode ? LL.mealPhotoScanner.scanBarcodeButton() : LL.mealPhotoScanner.scanLabelButton(), role: AlertButtonRole.Primary, result: 'scan' },
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: 'cancel' },
      ],
      implicitCancelResult: 'cancel',
    });
    if (this.viewDestroyed) return;
    if (action === 'scan') {
      this.redirectToPackagedScan(scanner);
    } else {
      // user declined — leave the captured photo up so they can retake
      this.retakePhoto();
    }
  }

  // Replace this scanner with the barcode or nutrition-label flow. We use
  // replaceTopAsync (not push) so this scanner leaves the navigation stack:
  // backing out of the barcode / label flow returns the user to the meal, never
  // to this scanner's camera. The picked / created food is delivered to the meal
  // by the caller's `onMealScanRedirectFood` handler (Edit Meal, Home, Nutrition
  // all have it), carrying this scanner's own returnContext (e.g. the target meal
  // id) so the food lands in the right meal.
  //
  // The barcode leg goes through the Foods page, whose search box the barcode
  // fills and whose result the user taps. The label leg opens the label scanner
  // directly (#5377): nothing is picked off a list there, and the food editor the
  // scanner turns into answers `onMealScanRedirectFood` with the same food list
  // result a pick gives, which is what `errandAnswerShape` asks for.
  private redirectToPackagedScan(scanner: 'barcode' | 'label') {
    this.telemetry.trackEvent(
      scanner === 'barcode' ? 'meal_photo_scan_product_label_to_barcode' : 'meal_photo_scan_product_label_to_label',
      {},
    );
    const returnContext = this.navParams.data.local?.returnContext;
    if (scanner === 'label') {
      void this.navCtrl.replaceTopAsync(ILabelScannerPage, {
        returnCallback: 'onMealScanRedirectFood',
        errandAnswerShape: ErrandAnswerShape.FoodsPageResult,
        local: { returnContext },
      });
      return;
    }
    void this.navCtrl.replaceTopAsync(IFoodsPage, {
      mode: 'select',
      openBarcodeScanner: true,
      returnCallback: 'onMealScanRedirectFood',
      local: { returnContext },
    });
  }

  // ── Add to meal ────────────────────────────────────────────────────

  async addToMeal() {
    // remember the user's choice for every ticked food the scan named. a food added by hand has no
    // AI name to remember it under, so it is left out (#6807)
    for (const item of rowsThatTeachThePreferenceMemory(this.resultItems)) {
      await this.prefMemory.recordPickAsync(item.aiName, item.foodId, item.foodHeader.name, this.userDb.database, this.logging);
    }

    const matchedFoods = scanRowsToMatchedFoods(this.resultItems);
    const addedByHand = this.resultItems.filter(item => item.checked && isHandAddedRow(item)).length;

    // the scan's grams against the grams the user adds, so real users' corrections can be measured
    // (#6808). only the rows the scan named: a food added by hand has no scan estimate (#6807)
    const grams = summarizeScanGramEdits(scannedRowsGoingIntoTheMeal(this.resultItems));
    this.telemetry.trackEvent('meal_photo_scan_added', {
      count: matchedFoods.length,
      addedByHand,
      aiGramsTotal: grams.aiGramsTotal,
      savedGramsTotal: grams.savedGramsTotal,
      rowsGramsChanged: grams.rowsGramsChanged,
      rowsWithPeel: grams.rowsWithPeel,
    });

    void this.navCtrl.popAsync({ matchedFoods } as MealPhotoScannerResult);
  }

  // ── Progress bar ──────────────────────────────────────────────────

  // end the analyzing state: fill the bar and drop the overlay
  private stopAnalyzing() {
    this.fakeProgress.stop();
    this.analyzing = false;
  }

  // per-tick sync from the shared fake-progress driver
  private onProgressTick() {
    if (this.viewDestroyed) {
      this.fakeProgress.stop();
      return;
    }
    this.cd.markForCheck();
  }

  // ── Alerts ────────────────────────────────────────────────────────

  // resetCamera: after the alert is dismissed, clear the captured photo and
  // restart the camera so an analyze error returns the user to a fresh camera
  // rather than leaving the failed photo on screen (owner decision 2026-06-15).
  private async showErrorAlertAsync(message: TranslatedString, resetCamera = false) {
    await this.alertCtrl.showAlertAsync({
      title: LL.mealPhotoScanner.errorTitle(),
      message,
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });
    if (this.viewDestroyed) return;
    if (resetCamera) this.retakePhoto();
  }

  // like showErrorAlertAsync, but for transient failures it adds the shared
  // "Try Again" button so the user can re-run the scan in place. Returns 'retry'
  // when tapped, null otherwise.
  private async showScanErrorAsync(message: TranslatedString, opts: { resetCamera?: boolean; offerRetry?: boolean } = {}): Promise<'retry' | null> {
    // without a retry button this is just the plain OK-only alert
    if (!opts.offerRetry) {
      await this.showErrorAlertAsync(message, opts.resetCamera);
      return null;
    }
    const choice = await showScanRetryAlertAsync(this.alertCtrl, LL.mealPhotoScanner.errorTitle(), message);
    if (this.viewDestroyed) return null;
    if (opts.resetCamera) this.retakePhoto();
    // zoneless + OnPush: stopAnalyzing() cleared the analyzing flag without a
    // change-detection tick, so repaint here to hide the analyzing overlay once
    // the alert is dismissed (a retry re-enters analyze which marks again).
    this.cd.markForCheck();
    return choice === 'retry' ? 'retry' : null;
  }

  // central handler for a failed meal-photo scan: consult the shared recovery
  // policy, show the right alert (Try Again for transient provider/network
  // failures), and re-run the scan when the user taps it. The no-foods content
  // path keeps its own alert + reset. The caller must have already stopped the
  // progress bar.
  private async handleScanFailureAsync(error: string | undefined) {
    await runScanFailureRecoveryAsync({
      error,
      isOnline: this.onlineState.isLikelyOnline(),
      showScanErrorAsync: alertOptions => this.showScanErrorAsync(LL.mealPhotoScanner.error(), alertOptions),
      scheduleOnNextTask: run => this.safeTimeout(run, 0),
      retryScan: () => { if (!this.viewDestroyed) void this.analyzePhoto(); },
    });
  }

  // ── Cleanup ────────────────────────────────────────────────────────

  /** cs-nav awaits ionViewCanLeave before starting the page transition;
   *  fire-and-forgets ionViewWillLeave. Await the native camera teardown
   *  here — the AVCapturePreviewLayer would otherwise stay visible on
   *  top of the incoming page during the route transition. */
  async ionViewCanLeave() {
    await this.cameraView?.stopCamera();
    return true;
  }

  async ionViewWillLeave() {
    this.fakeProgress.stop();
  }

  ngOnDestroy() {
    void this.cameraView?.stopCamera();
    this.fakeProgress.stop();
  }
}
